import { ADMIN_MODAL_TYPE } from '@data'
import { useAppointment, useMarkNotificationForNotifiable } from '@hook'
import { PencilIcon, Trash } from '@icons'
import { useModalStore } from '@modal'
import type { AppointmentIncluded, AppointmentResMock } from '@type/appointment.type'
import { Badge, Button, SideDrawer } from '@ui'
import InformationList from '@ui/InformationList'
import { parseIsoToLocalDate } from '@util'
import { Box, Spinner, Text } from 'grommet'
import moment from 'moment'
import type { CalendarEventDefaultData } from './CreateAppointment'
/** Build defaultData for CreateAppointment from single appointment API response + included */
function appointmentToCalendarDefaultData(
  appointment: AppointmentResMock | undefined,
  included: AppointmentIncluded[] | undefined
): CalendarEventDefaultData | undefined {
  if (!appointment) return undefined
  const staff = included?.find(({ type, id }) => type === 'staff' && id === appointment.relationships?.staff?.data?.id)
  const patient = included?.find(
    ({ type, id }) => type === 'patient' && id === appointment.relationships?.patient?.data?.id
  )
  const doctorName = staff?.attributes
    ? [staff.attributes.first_name, staff.attributes.last_name].filter(Boolean).join(' ').trim()
    : ''
  const patientName = patient?.attributes
    ? [patient.attributes.first_name, patient.attributes.last_name].filter(Boolean).join(' ').trim()
    : ''
  return {
    id: appointment.id,
    resourceId: appointment.relationships?.staff?.data?.id ?? '',
    title: appointment.attributes?.purpose,
    meta: {
      attributes: {
        appointment_at: appointment.attributes?.appointment_at,
        start_time: appointment.attributes?.start_time,
        end_time: appointment.attributes?.end_time,
        appointment_type: appointment.attributes?.appointment_type
      },
      doctorName,
      patientName,
      relationships: appointment.relationships
    },
    start: appointment.attributes?.start_time ? new Date(appointment.attributes.start_time) : undefined
  }
}

type Props = {
  onClose: () => void
  id?: string
}

export const AppoointmetDetails = ({ onClose, id }: Props) => {
  useMarkNotificationForNotifiable('Appointment', id)
  const { openModal } = useModalStore()
  const { singleAppointment, singleIncludedData, appointmentIsLoading } = useAppointment({ id })
  const getIncludedData = (data?: AppointmentIncluded[], user?: string) => {
    return data?.find(({ type }) => type === user)
  }
  const patientDetails = {
    Email: getIncludedData(singleIncludedData, 'patient')?.attributes?.email || '-',
    'Full name': `${getIncludedData(singleIncludedData, 'patient')?.attributes.first_name} ${getIncludedData(singleIncludedData, 'patient')?.attributes.last_name}`,
    'Phone Number': getIncludedData(singleIncludedData, 'patient')?.attributes.phone_number
  }
  const appointmentDetails = {
    'Assigned to': `${getIncludedData(singleIncludedData, 'staff')?.attributes.first_name} ${getIncludedData(singleIncludedData, 'staff')?.attributes.last_name}`,
    Date: moment(singleAppointment?.attributes.appointment_at).format('DD/MM/YYYY') || '-',
    status: <Badge dot label={singleAppointment?.meta?.status} status={singleAppointment?.meta?.status} />,
    time:
      singleAppointment?.attributes?.start_time && singleAppointment?.attributes?.end_time
        ? `${moment(parseIsoToLocalDate(singleAppointment.attributes.start_time)).format('hh:mm A')} - ${moment(parseIsoToLocalDate(singleAppointment.attributes.end_time)).format('hh:mm A')}`
        : '-'
  }
  const moreDetails = {
    'cancelled by': singleAppointment?.attributes?.cancelled_by,
    reason: singleAppointment?.attributes?.cancel_reason
  }

  const header = (
    <>
      {(singleAppointment?.meta?.status === 'upcoming' || singleAppointment?.meta?.status === 'cancelled') && (
        <Box direction="row" gap="12px">
          <Button
            icon={<PencilIcon />}
            kinds="secondary"
            label="Reschedule"
            onClick={() => {
              const defaultData = appointmentToCalendarDefaultData(singleAppointment, singleIncludedData)
              openModal(ADMIN_MODAL_TYPE.CREATE_APPOINTMENT, { id: id || '', defaultData })
            }}
            size={'sm'}
          />
          {singleAppointment?.meta?.status === 'upcoming' && (
            <Button
              icon={<Trash />}
              kinds="secondary"
              label="Cancel"
              onClick={() => {
                openModal(ADMIN_MODAL_TYPE.CANCEL_APPOINTMENT, { id: id || '', showStaff: true })
              }}
              size={'sm'}
              style={{
                color: '#D95069',
                fontSize: '14px',
                fontWeight: '400'
              }}
            />
          )}
        </Box>
      )}
    </>
  )

  return (
    <SideDrawer onClose={onClose} title="Appointment Details">
      <Box pad={{ horizontal: 'medium', vertical: '20px' }}>
        <Text margin={{ bottom: '16px' }}>Title: {singleAppointment?.attributes?.purpose}</Text>
        {header}
        {appointmentIsLoading ? (
          <Box align="center" justify="center" style={{ minHeight: '100px' }}>
            <Spinner height={'80px'} size="5px" width={'80px'} />
            <Text weight="bold">Loading ...</Text>
          </Box>
        ) : (
          <>
            <InformationList data={patientDetails} title="Patient Details" />
            <InformationList data={appointmentDetails} title="Appointment Details" />
            {singleAppointment?.meta?.status === 'cancelled' && <InformationList data={moreDetails} title="More" />}
          </>
        )}
      </Box>
    </SideDrawer>
  )
}
